import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import { ExportJobRetrieve } from "../../types/generated/api/ExportJobRetrieve"
import type { MenuOption } from "../Members"
import { ExportJobCard } from "./ExportJobCard"
import type { ExportJobContainerProps } from "./interfaces"

/**
 * Represents a container function that wraps the DataFile component.
 *
 * @param {ExportJobList} exportJob The export job associated with the data file.
 * @returns {React.ReactElement}
 */
export function Container(props: Readonly<ExportJobContainerProps>): React.ReactElement {
  const { exportJob, openDataFileHistory } = props
  const { id: projectId } = useParams()

  const { t } = useTranslation("datafiles")

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  // retrieve export job
  const { refetch } = useQuery<AxiosResponse<ExportJobRetrieve>, AxiosError>(
    ["export-job-retrieve", projectId, exportJob.uuid],
    () => KonanAPI.retrieveExportJob({ project_uuid: projectId as string, export_job_uuid: exportJob.uuid }),
    {
      enabled: false,
      // Downloading file from pre-signed url
      onSuccess: ({ data }) => {
        const link = document.createElement("a")
        link.href = data?.predictions_file as string

        // Start download
        link.click()

        // Clean up and remove the link
        link.remove()
      },
    },
  )

  const deleteExportJobMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      export_job_uuid: string
    }
  >(KonanAPI.deleteExportJob, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["data-files", projectId])

      NotificationUtils.toast(t("notifications.exportJobDeleted"), { severity: "success" })
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.exportJobDeleteError"), { severity: "error" })
    },
  })

  const cancelExportJobMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      export_job_uuid: string
    }
  >(KonanAPI.cancelExportJob, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["data-files", projectId])

      NotificationUtils.toast(t("notifications.exportJobDeleted"), { severity: "success" })
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.exportJobDeleteError"), { severity: "error" })
    },
  })

  const isJobInProgress = [ExportJobRetrieve.status.RUNNING, ExportJobRetrieve.status.PENDING].includes(
    ExportJobRetrieve.status[exportJob.status as keyof typeof ExportJobRetrieve.status],
  )

  const menuOptions = (): MenuOption[] => {
    const options: MenuOption[] = []

    if (isJobInProgress) {
      options.push({
        title: t("exportJobContainerMenu.cancelDataFile"),
        variant: "negative",
        onClick: () => {
          setIsDeletionDialogOpen(true)
        },
      })
    } else {
      options.push({
        title: t("exportJobContainerMenu.downloadHistory"),
        variant: "base",
        onClick: () => {
          openDataFileHistory?.(exportJob.name)
        },
      })

      options.push({
        title: t("exportJobContainerMenu.deleteDataFile"),
        variant: "negative",
        onClick: () => {
          setIsDeletionDialogOpen(true)
        },
      })
    }

    return options
  }

  const onDialogAccept = async (): Promise<void> => {
    if (!isJobInProgress) {
      await deleteExportJobMutation.mutateAsync({
        project_uuid: projectId as string,
        export_job_uuid: exportJob.uuid,
      })
    } else {
      await cancelExportJobMutation.mutateAsync({
        project_uuid: projectId as string,
        export_job_uuid: exportJob.uuid,
      })
    }
  }

  return (
    <Fragment>
      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          isLoading={deleteExportJobMutation.isLoading}
          onAccept={onDialogAccept}
          mode={!isJobInProgress ? "data-file-deletion" : "data-file-cancelation"}
          name={exportJob.name}
          open
          onClose={() => setIsDeletionDialogOpen(false)}
        />
      )}

      <ExportJobCard
        exportJob={exportJob}
        onDownloadInitiated={() => {
          refetch()
          NotificationUtils.toast(t("notifications.exportJobDownloadStarting"), { severity: "info" })
        }}
        menuOptions={menuOptions()}
      />
    </Fragment>
  )
}
